import { useQuery } from "@tanstack/react-query";
import { Order } from "@/pages/Orders/orders.types";
import { ORDER_SOURCES, OrderSource } from "@/services/orders/sources/order-source";
import { useSession } from "@/common/providers/session-provider";

export interface HardwareOrdersResult {
  orders: Order[];
  isLoading: boolean;
  hasError: boolean;
  degraded: boolean;
  refetch: () => void;
}

interface HardwareOrdersData {
  orders: Order[];
  degraded: boolean;
}

export const HARDWARE_ORDERS_QUERY_KEY = ["orders", "hardware"] as const;

export const SOURCE_FETCH_TIMEOUT_MS = 8000;

// A slow or unresponsive source must degrade this one list, not block it:
// without a per-source bound a single hung backend keeps the whole table in
// its loading state and hides every other source's orders.
const fetchWithTimeout = (source: OrderSource, login: string): Promise<Order[]> =>
  new Promise<Order[]>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`Order source "${source.id}" timed out after ${SOURCE_FETCH_TIMEOUT_MS}ms`)),
      SOURCE_FETCH_TIMEOUT_MS
    );
    source
      .fetch(login)
      .then((orders) => {
        clearTimeout(timer);
        resolve(orders);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });

export const useHardwareOrdersQuery = (): HardwareOrdersResult => {
  const { username, hasPermission } = useSession();

  const enabledSources = ORDER_SOURCES.filter((source) => source.isEnabled({ hasPermission }));

  const query = useQuery<HardwareOrdersData, Error>({
    queryKey: [...HARDWARE_ORDERS_QUERY_KEY, username ?? "", enabledSources.map((source) => source.id)],
    queryFn: async () => {
      const settled = await Promise.allSettled(
        enabledSources.map((source) => fetchWithTimeout(source, username ?? ""))
      );

      const failures = settled.filter(
        (result): result is PromiseRejectedResult => result.status === "rejected"
      );
      if (enabledSources.length > 0 && failures.length === enabledSources.length) {
        throw failures[0].reason;
      }

      const orders = settled
        .filter((result): result is PromiseFulfilledResult<Order[]> => result.status === "fulfilled")
        .flatMap((result) => result.value);

      orders.sort((left, right) => (right.requested?.on ?? "").localeCompare(left.requested?.on ?? ""));
      return { orders, degraded: failures.length > 0 };
    },
  });

  return {
    orders: query.data?.orders ?? [],
    isLoading: query.isLoading,
    hasError: query.isError,
    degraded: query.data?.degraded ?? false,
    refetch: () => void query.refetch(),
  };
};
